'use client';

/**
 * Shared render-job progress card — the ONE card every render/export surface
 * renders (docs/RENDER_STATUS_CONTRACT.md, Video Plan Phase 3).
 *
 * It consumes ONLY the `RenderJobView` contract shape for job state:
 *   - stage label + micro status label
 *   - progress bar (determinate when `progress` is a number, an honest
 *     shimmer when it's undefined — never a fake bar)
 *   - Cancel ONLY when `view.canCancel` (the adapter computed it per state)
 *   - Retry when `view.canRetry` (the retry ACTION is surface-owned → onRetry)
 *   - output link / poster when completed
 *
 * `title` / `accessory` are presentational slots for the host surface (e.g.
 * the media studio's model name + credits chip) — they carry no job state.
 */

import { useState } from 'react';
import { motion } from 'motion/react';
import {
  ArrowsClockwise,
  ArrowUpRight,
  CheckCircle,
  Download,
  FilmSlate,
  Prohibit,
  WarningCircle,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import type { RenderJobView } from '@/lib/render-job-view';

const KIND_LABEL: Record<RenderJobView['kind'], string> = {
  'narrated': 'Narrated video',
  'remotion-export': 'MP4 export',
  'hyperframes-export': 'MP4 export',
  'ai-clip': 'AI clip',
  'media-job': 'Media job',
};

const STATUS_LABEL: Record<RenderJobView['status'], string> = {
  queued: 'Queued',
  running: 'Rendering',
  completed: 'Ready',
  failed: 'Failed',
  canceled: 'Canceled',
};

function formatVideoDuration(sec: number) {
  if (sec < 60) return `${Math.round(sec)}s`;
  const mins = Math.floor(sec / 60);
  const rest = Math.round(sec - mins * 60);
  return `${mins}:${String(rest).padStart(2, '0')}`;
}

export function RenderJobCard({
  view,
  title,
  accessory,
  onRetry,
  className,
}: {
  view: RenderJobView;
  /** Optional headline owned by the host surface (e.g. video title, model id). */
  title?: string;
  /** Optional trailing slot (e.g. credits chip). Carries no job state. */
  accessory?: React.ReactNode;
  /** Retry action for failed jobs — only rendered when `view.canRetry`. */
  onRetry?: () => void;
  className?: string;
}) {
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const active = view.status === 'queued' || view.status === 'running';
  const failed = view.status === 'failed';
  const canceled = view.status === 'canceled';
  const completed = view.status === 'completed';

  const handleCancel = async () => {
    if (!view.cancel || canceling) return;
    setCanceling(true);
    setCancelError(null);
    try {
      await view.cancel();
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Cancel failed');
      setCanceling(false);
    }
  };

  const progressPct = typeof view.progress === 'number'
    ? Math.round(Math.max(0, Math.min(1, view.progress)) * 100)
    : null;

  const videoDurationSec = view.meta?.videoDurationSec;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border backdrop-blur-sm transition-colors',
        active && 'border-violet-500/25 bg-violet-500/[0.04]',
        completed && 'border-emerald-500/25 bg-emerald-500/[0.04]',
        failed && 'border-destructive/30 bg-destructive/[0.05]',
        canceled && 'border-border/80 bg-card/40',
        className,
      )}
    >
      {/* Quiet aurora wash while the job is live — decorative only. */}
      {active && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -inset-8 opacity-40"
          style={{
            background:
              'conic-gradient(from 0deg at 50% 50%, rgba(139,92,246,0.22), rgba(236,72,153,0.16), rgba(6,182,212,0.14), rgba(139,92,246,0.22))',
            filter: 'blur(32px)',
          }}
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 16, ease: 'linear' }}
        />
      )}

      <div className="relative flex items-start gap-3 px-4 py-3.5">
        {/* Status tile */}
        <div
          className={cn(
            'grid size-10 shrink-0 place-items-center rounded-xl border',
            active && 'border-violet-500/30 bg-violet-500/15',
            completed && 'border-emerald-500/30 bg-emerald-500/15',
            failed && 'border-destructive/30 bg-destructive/15',
            canceled && 'border-border/80 bg-muted/40',
          )}
        >
          {completed ? (
            <CheckCircle weight="duotone" className="size-5 text-emerald-400" />
          ) : failed ? (
            <WarningCircle weight="duotone" className="size-5 text-destructive" />
          ) : canceled ? (
            <Prohibit weight="duotone" className="size-5 text-muted-foreground" />
          ) : (
            <FilmSlate weight="duotone" className="size-5 text-violet-300" />
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          {/* Micro-label row */}
          <div className="flex items-center justify-between gap-3">
            <span
              className={cn(
                'text-[10px] font-bold uppercase tracking-[2px]',
                active && 'text-violet-400',
                completed && 'text-emerald-400',
                failed && 'text-destructive',
                canceled && 'text-muted-foreground',
              )}
            >
              {KIND_LABEL[view.kind]} · {STATUS_LABEL[view.status]}
            </span>
            {accessory && <span className="shrink-0">{accessory}</span>}
          </div>

          {/* Headline + stage */}
          {(title || view.stageLabel || view.error) && (
            <div className="space-y-0.5">
              {title && <p className="truncate text-sm font-medium text-foreground">{title}</p>}
              {active && view.stageLabel && (
                <p className="text-xs text-muted-foreground">{view.stageLabel}</p>
              )}
              {(failed || canceled) && (
                <p className={cn('text-xs', failed ? 'text-destructive/90' : 'text-muted-foreground')}>
                  {view.error ?? (canceled ? 'Canceled before it started.' : 'The render failed.')}
                </p>
              )}
              {completed && view.error && (
                <p className="text-xs text-amber-500/90">{view.error}</p>
              )}
            </div>
          )}

          {/* Progress bar — determinate when progress is known, honest shimmer otherwise. */}
          {active && (
            <div className="h-1.5 overflow-hidden rounded-full bg-border/50">
              {progressPct !== null ? (
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-violet-400 to-fuchsia-400"
                  initial={false}
                  animate={{ width: `${Math.max(2, progressPct)}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              ) : (
                <motion.div
                  className="h-full w-1/3 rounded-full bg-gradient-to-r from-transparent via-violet-400/80 to-transparent"
                  animate={{ x: ['-100%', '300%'] }}
                  transition={{ repeat: Infinity, duration: 1.4, ease: 'easeInOut' }}
                />
              )}
            </div>
          )}

          {/* Footer row: readouts + actions */}
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-[10px] tabular-nums text-muted-foreground/70">
              {active && progressPct !== null && <>{progressPct}%</>}
              {active && progressPct === null && <>working…</>}
              {videoDurationSec ? (
                <span className="ml-2">{formatVideoDuration(videoDurationSec)} video</span>
              ) : null}
            </span>

            <div className="flex shrink-0 items-center gap-1.5">
              {view.canCancel && view.cancel && (
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={canceling}
                  className="inline-flex h-7 items-center gap-1 rounded-full border border-border/80 bg-background/60 px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
                >
                  {canceling ? 'Canceling…' : 'Cancel'}
                </button>
              )}
              {view.canRetry && onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="inline-flex h-7 items-center gap-1.5 rounded-full bg-violet-500 px-3 text-xs font-semibold text-white transition-colors hover:bg-violet-400"
                >
                  <ArrowsClockwise weight="bold" className="size-3.5" />
                  Retry
                </button>
              )}
              {completed && view.outputUrl && (
                <a
                  href={view.outputUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-7 items-center gap-1.5 rounded-full bg-violet-500 px-3 text-xs font-semibold text-white transition-colors hover:bg-violet-400"
                  download
                >
                  {view.kind === 'media-job' ? (
                    <ArrowUpRight weight="bold" className="size-3.5" />
                  ) : (
                    <Download weight="bold" className="size-3.5" />
                  )}
                  {view.kind === 'media-job' ? 'Open' : 'Download MP4'}
                </a>
              )}
            </div>
          </div>

          {cancelError && (
            <p className="text-xs text-destructive/90">{cancelError}</p>
          )}

          {/* Poster thumbnail on completion, when the pipeline provides one. */}
          {completed && view.posterUrl && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={view.posterUrl}
              alt=""
              className="mt-1 max-h-32 w-auto rounded-lg border border-border/60"
            />
          )}
        </div>
      </div>
    </div>
  );
}
